import {Inject, Injectable, Injector, Type} from "@angular/core";
import {
    AbstractMessaging,
    CONTRIB_PROXY_END_POINT,
    HTTP_METHOD,
    ITCIActionResult,
    ITCIApplication,
    ITCIBaseServiceContribution,
    ITCIConnectorContribution,
    ITCIContribActionProvider,
    ITCIContribFieldProvider,
    ITCIContribRemoveProvider,
    ITCIContributionTypes,
    ITCIContribValidationProvider,
    ITCIFieldValidationResult,
    ITCIModelService,
    ITCIServiceContributionType,
    ITCIServiceHandlerContribution,
    ITCIServiceProviderContribution,
    STRING_MAP
} from "@tci/common";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { TciContribService } from "./tci-contrib.service";
import { Http, RequestOptions, RequestOptionsArgs, Response } from "@angular/http";
import { } from "reflect-metadata"; // do not remove this line

// tslint:disable:max-line-length
/**
 * Abstract Validation Provider
 */
export abstract class AbstractTciContribValidationProvider implements ITCIContribValidationProvider {
    /**
     * constructor
     * @param injector
     * @param http
     */

    // constructor(private injector: Injector, private http: Http) { }
    /**
     * validate
     * @param context
     */
    abstract validate(context: ITCIContributionTypes): ITCIFieldValidationResult | Observable<ITCIFieldValidationResult>;
}

/**
 * interface Validation provider map
 */
export interface ITCIContribValidationProviderMap {
    field: string;
    useClass: Type<ITCIContribValidationProvider>;
}


/**
 * Abstract contrib field provider
 */
export abstract class AbstractTciContribFieldProvider implements ITCIContribFieldProvider {
    /**
     * constructor
     * @param injector
     * @param http
     */

    // constructor(private injector: Injector, private http: Http) { }
    /**
     * getFieldValue
     * @param context
     */
    abstract getFieldValue(context: ITCIContributionTypes): any | Observable<any>;
}

/**
 * field provider map
 */
export interface ITCIContribFieldProviderMap {
    field: string;
    useClass: Type<ITCIContribFieldProvider>;
}

/**
 * Action Provider Map
 */
export interface ITCIContribActionProviderMap {
    /**
     * Field Name
     */
    field: string;
    /**
     * Action Provider Class
     */
    useClass: Type<ITCIContribActionProvider>;
}

/**
 * Abstract Action Provider
 */
export abstract class AbstractTciContribActionProvider implements ITCIContribActionProvider {
    /**
     * abstract handle action function
     * @param {ITCIContributionTypes} context
     */
    abstract handleAction(context: ITCIContributionTypes): any | Observable<any>;
}

/**
 * Abstract Remove Provider
 */
export abstract class AbstractTciContribRemoveProvider implements ITCIContribRemoveProvider {
    /**
     * abstract handle remove function
     * @param {ITCIContributionTypes} context
     * @return {any | Observable<any>}
     */
    abstract handleRemove(context: ITCIContributionTypes): ITCIActionResult | Observable<ITCIActionResult>;
}

/**
 * @private
 * @type {symbol}
 */
const TCI_CONTRIB_SYMBOL = Symbol("@TciContrib");

/**
 * Type of the TciConnectorContrib metadata.
 *
 */
export interface TciContribMetaData {
    /**
     * Defines the set of validation providers that are available in the contribution
     * of this module.
     *
     * ## Simple Example
     *
     * Here is an example of a validation provider class that can be injected:
     *
     * ```
     *
     *  @Injectable()
     * export class Field1ValidationProvider extends AbstractTciContribValidationProvider {
     *      validate(context: ITCIContributionContext): Observable<ITCIValidationResult> {
     *          return Observable.create(observer => {
     *               let vresult: ITCIValidationResult = TCIValidationResult.newValidationResult();
     *                      observer.next(vresult);
     *           });
     *      }
     * }
     *  @TciContrib({
     *   validationProviders: [
     *      {
     *          field: "field1",
     *          useClass: Field1ValidationProvider
     *      }
     *   ],
     *   fieldProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1Provider
     *      }
     *   ]
     * })
     *  @Injectable()
     * export class TestContribService extends TciServiceContribution {}
     * ```
     */
    validationProviders?: ITCIContribValidationProviderMap[];
    /**
     * @description Defines the set of validation providers that are available in the contribution
     * of this module.
     *
     * ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *  @Injectable()
     * export class Field1Provider extends AbstractTciContribFieldProvider {
     *   getFieldValue(context: ITCIActivityContribution): Observable<string[]> {
     *      return Observable.create(observer => {
     *        observer.next("");
     *     });
     *  }
     * }
     *  @TciContrib({
     *   validationProviders: [
     *      {
     *          field: "field1",
     *          useClass: Field1ValidationProvider
     *      }
     *   ],
     *   fieldProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1Provider
     *      }
     *   ]
     * })
     *  @Injectable()
     * export class TestContribService extends TciServiceContribution {}
     * ```
     */
    fieldProviders?: ITCIContribFieldProviderMap[];
    /**
     * @description Defines the set of action providers that are available in the contribution
     * of this module.
     *
     * ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *  @Injectable()
     * export class Field1ActionProvider extends AbstractTciActionProvider {
     *   handleAction(context: ITCIActivityContribution): Observable<string[]> {
     *      return Observable.create(observer => {
     *        observer.next("");
     *     });
     *  }
     * }
     *  @TciContrib({
     *   validationProviders: [
     *      {
     *          field: "field1",
     *          useClass: Field1ValidationProvider
     *      }
     *   ],
     *   fieldProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1Provider
     *      }
     *   ]
     *   actionProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1ActionProvider
     *      }
     *   ]
     * })
     *  @Injectable()
     * export class RestContribService extends TciServiceProviderContribution {}
     * ```
     */
    actionProviders?: ITCIContribActionProviderMap[];
    /**
     * @description Optional remove handler for handling cleanup actions on external domains, invoked when the contribution
     * instance is deleted. The key information for delete comes from the context itself.
     * @example
     * ## Simple Example
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *  @Injectable()
     * export class MyConnectionRemoveProvider extends AbstractActionProvider {
     *   handleRemove(context: IConnectorContribution): Observable<string[]> {
     *      return Observable.create(observer => {
     *        observer.next("");
     *     });
     *  }
     * }
     *  @TciContrib({
     *   validationProviders: [
     *      {
     *          field: "field1",
     *          useClass: Field1ValidationProvider
     *      }
     *   ],
     *   fieldProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1Provider
     *      }
     *   ],
     *   actionProviders: [
     *      {
     *       field: "field1",
     *       useClass: Field1ActionProvider
     *      }
     *   ],
     *   removeProvider : MyConnectionRemoveProvider
     * })
     * ```
     *
     */
    removeProvider?: Type<ITCIContribRemoveProvider>;

}

/**
 * The [[TciServiceContribution]] being the abstract base class is extended  by a contribution provider handler [[TciServiceHandlerContribution]]
 * or a [[TciServiceProviderContribution]]. The user can choose either implmentation model to suit their needs.
 *
 * ## [[TciServiceHandlerContribution]]
 * The [[TciServiceHandlerContribution]] represents one way of providing contribution where the contribution will have its own single implementation
 * of [[TciServiceHandlerContribution]] and the @[[Injectable]] class decorator will tell the loader that it is loadable by Angular and the single class
 * will provide handler functions for value, validation and action. The @TciContrib({}) deoorator with null metadata indicates it is a one abstract class
 * [[TciServiceHandlerContribution]] implmentation.
 *
 *  ### Example [[TciServiceHandlerContribution]]  Contribution Handler Typescript file
 *  ```
 *  import {Injectable, Injector} from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import {ValidationResult} from "@tci/common";
 *  import {ITCIActivityContribution, ActionResult, ITCIActionResult} from "@tci/common";
 *  import {WiContrib, TciServiceHandlerContribution} from "../../wi-contrib";
 *  import {Http} from "@angular/http";
 *  import {IValidationResult} from "@tci/common";
 *
 *  @TciContrib({})
 *  @Injectable()
 *  export class RestContributionHandler extends TciServiceHandlerContribution {
 *      constructor(injector: Injector, http: Http) {
 *       super(injector, http);
 *   }
 *
 *   value = (fieldName: string, context: IActivityContribution): Observable<any> | any => {
 *       return Observable.create(observer => {
 *           observer.next("");
 *      });
 *   }
 *
 *  validate = (fieldName: string, context: IActivityContribution): Observable<IValidationResult> | IValidationResult => {
 *       return Observable.create(observer => {
 *            let vresult: IValidationResult = ValidationResult.newValidationResult();
 *            observer.next(vresult);
 *        });
 *   }
 *
 *    action = (actionId: string, context: IActivityContribution): Observable<IActionResult> | IActionResult => {
 *       return Observable.create(observer => {
 *            let aresult: IActionResult = ActionResult.newActionResult();
 *            observer.next(aresult);
 *        });
 *    }
 *  }
 *  ```
 *  ### Example [[TciServiceHandlerContribution]]  Contribution Module Typescript file
 *
 *  ```
 *  import { HttpModule } from "@angular/http";
 *  import { NgModule } from "@angular/core";
 *  import { CommonModule } from "@angular/common";
 *  import { RestContributionHandler } from "./rest-contrib";
 *  import {TciServiceContribution} from "../../wi-contrib";
 *  @NgModule({
 *    imports: [CommonModule,HttpModule],
 *    exports: [],
 *    declarations: [],
 *    entryComponents: [],
 *    providers: [{
 *      provide: TciServiceContribution,
 *      useClass: RestContributionHandler
 *    }],
 *    bootstrap: []
 *  })
 *  export class RestContribModule {}
 *  ```
 *
 * ## [[TciServiceProviderContribution]]
 * The [[TciServiceProviderContribution]] represents another way of providing contribution
 * where each field will have its own [[AbstractContribFieldProvider]],[[AbstractContribValidationProvider]],[[AbstractActionProvider]] and the [[TciServiceProviderContribution]]
 * implementation with @[[WiContrib]] decorator will tell the loader that the module is of type [[TciServiceProviderContribution]]. Each field value,validation
 * and action provider is also a Angular injectable Service and must be decorated by @Injectable(). The contribution service is also a Angular injectable service
 * as decorated with @Injectable() and also a Contribution Service with @TciContrib(metadata) deoorator.
 *
 *  ### Example [[TciServiceProviderContribution]]  Contribution Service Typescript file
 *
 *  ```
 *  import { HttpModule } from "@angular/http";
 *  import { NgModule } from "@angular/core";
 *  import { CommonModule } from "@angular/common";
 *  import { RestContributionHandler } from "./timer-contrib";
 *  import {TciServiceContribution} from "wi-contrib";
 *  @NgModule({
 *  imports: [CommonModule,HttpModule],
 *  exports: [],
 *  declarations: [],
 *  entryComponents: [],
 *  providers: [
 *   {
 *     provide: TciServiceContribution,
 *     useClass: TimerContribService
 *   },
 *   {
 *     provide: Field1Provider,
 *     useClass: Field1Provider
 *   },
 *   {
 *     provide: Field1ValidationProvider,
 *     useClass: Field1ValidationProvider
 *   }],
 *  bootstrap: []
 *  })
 *  export class RestContribModule {}
 *  ```
 *
 *  ### Example [[TciServiceProviderContribution]]  Contribution Module Typescript file
 *  ```
 *  import { ValidationResult } from "wi-studio/common/models/validation";
 *  import { IActivityContribution, IContributionContext } from "wi-studio/common/models/contrib";
 *  import { Injectable, Inject, Injector } from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import { Http } from "@angular/http";
 *  import { AbstractContribFieldProvider, AbstractContribValidationProvider, WiContrib, TciServiceContribution } from "wi-studio/app/wi-contrib";
 *  import {IValidationResult} from "wi-studio/common/models/validation";
 *
 *  @Injectable()
 *  export class Field1Provider extends AbstractContribFieldProvider {
 *    getFieldValue(context: IActivityContribution): Observable<string[]> {
 *        return Observable.create(observer => {
 *            observer.next("");
 *       });
 *    }}
 *  @Injectable()
 *  export class Field1ValidationProvider extends AbstractContribValidationProvider {
 *     validate(context: IContributionContext): Observable<IValidationResult> {
 *         return Observable.create(observer => {
 *             // tslint:disable-next-line:no-bitwise
 *             let vresult: IValidationResult = ValidationResult.newValidationResult();
 *             observer.next(vresult);
 *         });
 *     }}
 *
 *  @TciContrib({
 *     validationProviders: [{
 *            field: "field1",
 *             useClass: Field1ValidationProvider
 *         }],
 *     fieldProviders: [{
 *             field: "field1",
 *             useClass: Field1Provider
 *         }]
 *  })
 *  @Injectable()
 *  export class TimerContribService extends TciServiceProviderContribution {}
 * ```
 */
@Injectable()
export abstract class TciServiceContribution implements ITCIBaseServiceContribution {
    /**
     * constructor
     * @param _injector
     * @param _http
     * @param _modelService
     */
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http, @Inject(AbstractTciContribModelService) private _modelService?: ITCIModelService) {
    }

    /**
     *  Returns Type of contribution implementation , handler [[TciServiceProviderContribution]] or provider [[TciServiceHandlerContribution]]
     * @return {IServiceContributionType}
     */
    public getType(): ITCIServiceContributionType {
        const metadata = this.getAnnotations();
        if (metadata && (metadata.fieldProviders || metadata.validationProviders || metadata.actionProviders || metadata.removeProvider)) {
            return ITCIServiceContributionType.Provider;
        } else {
            return ITCIServiceContributionType.Handler;
        }
    }

    /**
     * get annotation from metadata
     * @protected
     */
    protected getAnnotations(): TciContribMetaData {
        const metadata = <TciContribMetaData>Reflect.getMetadata("custom:annotation", this.constructor, TCI_CONTRIB_SYMBOL);
        return metadata;
    }

    /**
     * Returns the injector
     * @protected
     * @return {Injector}
     */
    protected getInjector(): Injector {
        return this._injector;
    }

    /**
     * Returns the [[Http]] object
     * @protected
     * @return {Http}
     */
    protected getHttp(): Http {
        return this._http;
    }

    /**
     * Returns the [[WiContribModelService]] object
     * @return {IModelService}
     */
    protected getModelService(): ITCIModelService {
        return this._modelService;
    }

}


/**
 * @description Abstract Service Contribution
 * ## [[TciServiceProviderContribution]]
 * The [[TciServiceProviderContribution]] represents another way of providing contribution
 * where each field will have its own [[AbstractContribFieldProvider]],[[AbstractContribValidationProvider]],[[AbstractActionProvider]] and the [[TciServiceProviderContribution]]
 * implementation with @[[WiContrib]] decorator will tell the loader that the module is of type [[TciServiceProviderContribution]]. Each field value,validation
 * and action provider is also a Angular injectable Service and must be decorated by @Injectable(). The contribution service is also a Angular injectable service
 * as decorated with @Injectable() and also a Contribution Service with @TciContrib(metadata) deoorator.
 *
 *  ### Example [[TciServiceProviderContribution]]  Contribution Service Typescript file
 *
 *  ```
 *  import { HttpModule } from "@angular/http";
 *  import { NgModule } from "@angular/core";
 *  import { CommonModule } from "@angular/common";
 *  import { RestContributionHandler } from "./timer-contrib";
 *  import {TciServiceContribution} from "wi-contrib";
 *  @NgModule({
 *  imports: [CommonModule,HttpModule],
 *  exports: [],
 *  declarations: [],
 *  entryComponents: [],
 *  providers: [
 *   {
 *     provide: TciServiceContribution,
 *     useClass: TimerContribService
 *   },
 *   {
 *     provide: Field1Provider,
 *     useClass: Field1Provider
 *   },
 *   {
 *     provide: Field1ValidationProvider,
 *     useClass: Field1ValidationProvider
 *   }],
 *  bootstrap: []
 *  })
 *  export class RestContribModule {}
 *  ```
 *
 *  ### Example [[TciServiceProviderContribution]]  Contribution Module Typescript file
 *  ```
 *  import { ValidationResult } from "wi-studio/common/models/validation";
 *  import { IActivityContribution, IContributionContext } from "wi-studio/common/models/contrib";
 *  import { Injectable, Inject, Injector } from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import { Http } from "@angular/http";
 *  import { AbstractContribFieldProvider, AbstractContribValidationProvider, WiContrib, TciServiceContribution } from "wi-studio/app/wi-contrib";
 *  import {IValidationResult} from "wi-studio/common/models/validation";
 *
 *  @Injectable()
 *  export class Field1Provider extends AbstractContribFieldProvider {
 *    getFieldValue(context: IActivityContribution): Observable<string[]> {
 *        return Observable.create(observer => {
 *            observer.next("");
 *       });
 *    }}
 *  @Injectable()
 *  export class Field1ValidationProvider extends AbstractContribValidationProvider {
 *     validate(context: IContributionContext): Observable<IValidationResult> {
 *         return Observable.create(observer => {
 *             // tslint:disable-next-line:no-bitwise
 *             let vresult: IValidationResult = ValidationResult.newValidationResult();
 *             observer.next(vresult);
 *         });
 *     }}
 *
 *  @TciContrib({
 *     validationProviders: [{
 *            field: "field1",
 *             useClass: Field1ValidationProvider
 *         }],
 *     fieldProviders: [{
 *             field: "field1",
 *             useClass: Field1Provider
 *         }]
 *  })
 *  @Injectable()
 *  export class TimerContribService extends TciServiceProviderContribution {}
 * ```
 */
@Injectable()
export abstract class TciServiceProviderContribution extends TciServiceContribution implements ITCIServiceProviderContribution {
    /**
     * constructor
     * @param injector
     * @param http
     * @param modelService
     */
    constructor(@Inject(Injector) injector?: Injector, @Inject(Http) http?: Http, @Inject(AbstractTciContribModelService) modelService?: ITCIModelService) {
        super(injector, http, modelService);
    }

    /**
     * get field provider
     * @private
     * @param fieldName
     */
    getFieldProvider(fieldName: string): ITCIContribFieldProvider {
        const metadata = this.getAnnotations();
        if (metadata.fieldProviders && metadata.fieldProviders.length > 0) {
            for (let i = 0; i < metadata.fieldProviders.length; i++) {
                if (metadata.fieldProviders[i].field === fieldName) {
                    return this.getInjector().get(metadata.fieldProviders[i].useClass);
                }
            }
        }
        return null;
    }

    /**
     * get validation provider
     * @private
     * @param fieldName
     */
    getValidationProvider(fieldName: string): ITCIContribValidationProvider {
        const metadata = this.getAnnotations();
        if (metadata.validationProviders && metadata.validationProviders.length > 0) {
            for (let i = 0; i < metadata.validationProviders.length; i++) {
                if (metadata.validationProviders[i].field === fieldName) {
                    return this.getInjector().get(metadata.validationProviders[i].useClass);
                }
            }
        }
        return null;
    }

    /**
     * get action provider
     * @private
     * @param fieldName
     */
    getActionHandler(fieldName: string): ITCIContribActionProvider {
        const metadata = this.getAnnotations();
        if (metadata.actionProviders && metadata.actionProviders.length > 0) {
            for (let i = 0; i < metadata.actionProviders.length; i++) {
                if (metadata.actionProviders[i].field === fieldName) {
                    return this.getInjector().get(metadata.actionProviders[i].useClass);
                }
            }
        }
        return null;
    }

    /**
     * get the remove provider
     * @return {ITCIContribRemoveProvider}
     */
    getRemoveProvider(): ITCIContribRemoveProvider {
        const metadata = this.getAnnotations();
        if (metadata.removeProvider) {
            return this.getInjector().get(metadata.removeProvider);
        }
        return null;
    }

}


/**
 * @description Abstract Service Handler Contribution
 * ## [[TciServiceHandlerContribution]]
 * The [[TciServiceHandlerContribution]] represents one way of providing contribution where the contribution will have its own single implementation
 * of [[TciServiceHandlerContribution]] and the @[[Injectable]] class decorator will tell the loader that it is loadable by Angular and the single class
 * will provide handler functions for value, validation and action. The @TciContrib({}) deoorator with null metadata indicates it is a one abstract class
 * [[TciServiceHandlerContribution]] implmentation.
 *
 *  ### Example [[TciServiceHandlerContribution]]  Contribution Handler Typescript file
 *  ```
 *  import {Injectable, Injector} from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import {ValidationResult} from "@tci/common";
 *  import {IActivityContribution, ActionResult, IActionResult} from "@tci/common";
 *  import {WiContrib, TciServiceHandlerContribution} from "../../wi-contrib";
 *  import {Http} from "@angular/http";
 *  import {IValidationResult} from "@tci/common";
 *
 *  @TciContrib({})
 *  @Injectable()
 *  export class RestContributionHandler extends TciServiceHandlerContribution {
 *      constructor(injector: Injector, http: Http) {
 *       super(injector, http);
 *   }
 *
 *   value = (fieldName: string, context: IActivityContribution): Observable<any> | any => {
 *       return Observable.create(observer => {
 *           observer.next("");
 *      });
 *   }
 *
 *  validate = (fieldName: string, context: IActivityContribution): Observable<IValidationResult> | IValidationResult => {
 *       return Observable.create(observer => {
 *            let vresult: IValidationResult = ValidationResult.newValidationResult();
 *            observer.next(vresult);
 *        });
 *   }
 *
 *    action = (actionId: string, context: IActivityContribution): Observable<IActionResult> | IActionResult => {
 *       return Observable.create(observer => {
 *            let aresult: IActionResult = ActionResult.newActionResult();
 *            observer.next(aresult);
 *        });
 *    }
 *  }
 *  ```
 *  ### Example [[TciServiceHandlerContribution]]  Contribution Module Typescript file
 *
 *  ```
 *  import { HttpModule } from "@angular/http";
 *  import { NgModule } from "@angular/core";
 *  import { CommonModule } from "@angular/common";
 *  import { RestContributionHandler } from "./rest-contrib";
 *  import {TciServiceContribution} from "../../wi-contrib";
 *  @NgModule({
 *    imports: [CommonModule,HttpModule],
 *    exports: [],
 *    declarations: [],
 *    entryComponents: [],
 *    providers: [{
 *      provide: TciServiceContribution,
 *      useClass: RestContributionHandler
 *    }],
 *    bootstrap: []
 *  })
 *  export class RestContribModule {}
 *  ```
 *
 */
@Injectable()
export abstract class TciServiceHandlerContribution extends TciServiceContribution implements ITCIServiceHandlerContribution {
    /**
     * @constructor
     * *  ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *    @Injectable()
     *   export class RestContributionHandler extends TciServiceHandlerContribution {
     *   constructor(injector: Injector, http: Http, modelService: IModelService) {
     *     super(injector, http);
     *  }
     * ...
     * }
     * ```
     * @param injector
     * @param http
     * @param modelService
     */
    constructor(@Inject(Injector) injector?: Injector, @Inject(Http) http?: Http, @Inject(TciContribModelService) modelService?: ITCIModelService) {
        super(injector, http, modelService);
    }

    /**
     *  @description Provide values for a specified field name parameter
     *  ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *    @Injectable()
     *   export class RestContributionHandler extends TciServiceHandlerContribution {
     *   constructor(injector: Injector, http: Http) {
     *     super(injector, http);
     *  }
     *
     *  value = (fieldName: string, context: IActivityContribution): Observable<any> | any => {
     *    return Observable.create(observer => {
     *          observer.next("");
     *    });
     *  }
     *
     * }
     * ```
     * @param fieldName {{string}}
     * @param context {{IContributionTypes}}
     * @return {{Observable<any> | any}}
     */
    public value: (fieldName: string, context: ITCIContributionTypes) => Observable<any> | any;
    /**
     * @description Validate a specified field name parameter
     * ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *    @Injectable()
     *   export class RestContributionHandler extends TciServiceHandlerContribution {
     *   constructor(injector: Injector, http: Http) {
     *     super(injector, http);
     *  }
     * ...
     *  validate = (fieldName: string, context: IActivityContribution): Observable<IValidationResult> | IValidationResult => {
     *   return Observable.create(observer => {
     *       let vresult: IValidationResult = ValidationResult.newValidationResult();
     *        observer.next(vresult);
     *    });
     *  }
     *
     * }
     * ```
     *
     * @param fieldName {{string}}
     * @param context {{IContributionTypes}}
     * @return {{Observable<IValidationResult> | IValidationResult}}
     */
    public validate: (fieldName: string, context: ITCIContributionTypes) => Observable<ITCIFieldValidationResult> | ITCIFieldValidationResult;
    /**
     * @description Optional action handler for form buttons
     *
     * ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *    @Injectable()
     *   export class RestContributionHandler extends TciServiceHandlerContribution {
     *   constructor(injector: Injector, http: Http) {
     *     super(injector, http);
     *  }
     *
     * ...
     *  action = (actionId: string, context: IActivityContribution): Observable<IActionResult> | IActionResult => {
     *    return Observable.create(observer => {
     *        let aresult: IActionResult = ActionResult.newActionResult();
     *        observer.next(aresult);
     *    });
     *   }
     * }
     * ```
     *
     * @param actionId
     * @param context
     * @return {{Observable<IActionResult> | IActionResult}}
     */
    public action?: (actionId: string, context: ITCIContributionTypes) => Observable<ITCIActionResult> | ITCIActionResult;

    /**
     * @description Optional remove handler for handling cleanup actions on external domains, invoked when the contribution
     * instance is deleted. The key information for delete comes from the context itself.
     * @example
     * ## Simple Example
     *
     * Here is an example of a field value provider class that can be injected:
     *
     * ```
     *
     *    @Injectable()
     *   export class MyConnectionHandler extends TciServiceHandlerContribution {
     *   constructor(injector: Injector, http: Http) {
     *     super(injector, http);
     *  }
     *
     * ...
     *  remove = (context: IConnectorContribution): Observable<IActionResult> | IActionResult => {
     *    return Observable.create(observer => {
     *        let aresult: IActionResult = ActionResult.newActionResult();
     *        observer.next(aresult);
     *    });
     *   }
     * }
     * ```
     * @param {IContributionTypes} context
     * @return {Observable<IActionResult> | IActionResult}
     */
    public remove?: (context: ITCIContributionTypes) => Observable<ITCIActionResult> | ITCIActionResult;

}


/**
 * @description This decorator and annotation indicates the typescript class is a Web Integrator contribution
 * ## Example
 * ```
 *
 *   @TciContrib()
 *   class RestService {}
 * ```
 *
 *
 * @param {TciContribMetaData} metadata
 * @return {(target: Function) => void}
 * @constructor
 */
export function WiContrib(metadata: TciContribMetaData): (target: Function) => void {
    return (target: Function): any => {
        // console.log("WiContrib(" + metadata + ") called on: ", target);
        Reflect.defineMetadata("custom:annotation", metadata, target, TCI_CONTRIB_SYMBOL);
        return target;
    };
}

/**
 *  Contribution Utility
 *  This utility class provides functions to get User defined connections,
 *  Server Configuration, Context Object unique id's
 */
export class TciContributionUtils {
    /**
     * Gets a list of available connections for a given connector type
     * @param connector_type
     * @return {Array}
     */
    public static getConnections(http: Http, connector_type?: string): Observable<ITCIConnectorContribution[]> {
        return Observable.create(observer => {
            let url = "/wistudio/v1/connections";
            if (connector_type) {
                url += "?type=" + connector_type;
            }
            http.get(url)
                .map(data => {
                    const connections = data.json(), connectionResult = [];
                    for (const connection of connections) {
                        if (connection && connection.connector) {
                            connectionResult.push(connection.connector);
                        }
                    }
                    return connectionResult;
                })
                .subscribe((connections) => {
                    observer.next(connections);
                }, (err) => {
                    observer.error(err);
                }, () => {
                    observer.complete();
                });
        });
    }

    /**
     * Gets a connection for a given connection name
     * @param connection_id
     * @return {null}
     */
    public static getConnection(http: Http, connection_id: string): Observable<ITCIConnectorContribution> | Observable<any> {
        return Observable.create(observer => {
            let url = "/wistudio/v1/connections";
            if (connection_id) {
                url += "/" + connection_id;
                http.get(url).subscribe((rsp) => {
                    if (rsp.status === 200) {
                        const conns = rsp.json();
                        if (conns && conns.connector) {
                            observer.next(conns.connector);
                        } else {
                            observer.next({});
                        }
                    } else {
                        observer.error({
                            code: rsp.status,
                            message: "Fail to get connection with name",
                            details: rsp.json()
                        });
                    }
                });
            } else {
                observer.error({ code: 400, message: "Param connection_id is required" });
            }
        });
    }


    /**
     * Return the unique id for a given context object
     * @param {IContributionTypes} ctx
     * @return {string}
     */
    public static getUniqueId(ctx: ITCIContributionTypes): string {
        return (<any>ctx).id;
    }
}

/**
 * Proxy Cross Origin Resource Sharing Utility
 * @desc This utility routes client side HTTP requests via a Server proxy to SAAS
 * providers which have CORS restrictions when accessing the APIs.
 */
export class TciProxyCORSUtils {
    queryParams: STRING_MAP<string>;
    headers: STRING_MAP<string>;
    method: HTTP_METHOD = HTTP_METHOD.get;
    data: any;

    /**
     * Create Http Request
     * @param http
     * @param remoteUrl Remote URL is the URL from r
     * @return {TciProxyCORSUtils}
     */
    public static createRequest(http: Http, remoteUrl: string): TciProxyCORSUtils {
        return new TciProxyCORSUtils(http, remoteUrl);
    }

    /**
     * Returns the http connection object
     * @return {Http}
     */
    public getHttp(): Http {
        return this.http;
    }

    /**
     * Constructor
     * @param http
     * @param remoteUrl
     */
    constructor(public http: Http, public remoteUrl: string) {
        this.headers = <STRING_MAP<string>>{};
        this.queryParams = <STRING_MAP<string>>{};
    }

    /**
     * Add Query Parameters
     * @param param
     * @param value
     * @return {CORSUtils}
     */
    addQueryParams(param: string, value: string): TciProxyCORSUtils {
        this.queryParams[param] = value;
        return this;
    }

    /**
     * Add Header key value
     * @param header
     * @param value
     * @return {CORSUtils}
     */
    addHeader(header: string, value: string): TciProxyCORSUtils {
        this.headers[header] = value;
        return this;
    }

    /**
     *  Add HTTP method {HTTP_METHOD}
     * @param method
     */
    addMethod(method: HTTP_METHOD): TciProxyCORSUtils {
        this.method = method;
        return this;
    }

    /**
     * Add Http Body
     * @param data
     */
    addBody(data: any): TciProxyCORSUtils {
        this.data = data;
        return this;
    }

    /**
     * Send Http Request and return and Observable for Response
     * @return {Observable<Response>}
     */
    send(): Observable<Response> {

        const headers = new Headers();
        for (const key in this.headers) {
            if (this.headers.hasOwnProperty(key)) {
                const value = this.headers[key];
                headers.append(key, value);
            }
        }

        if (this.queryParams) {
            this.remoteUrl = this.buildUrl(this.remoteUrl, this.queryParams);
        }

        const options = new RequestOptions(<RequestOptionsArgs><any>{ headers: headers });

        if (this.method.toLowerCase() === HTTP_METHOD.get) {
            return this.http.get(CONTRIB_PROXY_END_POINT + this.remoteUrl, options);
        } else if (this.method.toLowerCase() === HTTP_METHOD.post) {
            return this.http.post(CONTRIB_PROXY_END_POINT + this.remoteUrl, this.data, options);
        } else if (this.method.toLowerCase() === HTTP_METHOD.put) {
            return this.http.put(CONTRIB_PROXY_END_POINT + this.remoteUrl, this.data, options);
        } else if (this.method.toLowerCase() === HTTP_METHOD.delete) {
            return this.http.delete(CONTRIB_PROXY_END_POINT + this.remoteUrl, options);
        }
    }

    /**
     *  Build URL
     * @param {string} url
     * @param {STRING_MAP<string>} parameters
     * @return {string}
     */
    buildUrl(url: string, parameters: STRING_MAP<string>) {
        let queryString = "";
        for (const key in parameters) {
            if (parameters.hasOwnProperty(key)) {
                const value = parameters[key];
                queryString += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
            }
        }
        if (queryString.length > 0) {
            queryString = queryString.substring(0, queryString.length - 1);
            url = url + "?" + queryString;
        }
        return url;
    }

}

/**
 * NgUiContrib Messaging implementation
 * @description There can be different messaging use cases where different type of Subjects can be used
 * i.e. {Rx.Subject} or {Rx.BehaviorSubject} which determines what behavior is required for the use case.
 *
 * @example:
 * ```
 *  // Field messaging example
 *  let messaging = new NgUiContribMessaging();
 *  // Emitting data
 *  messaging.emit<any>("Field1",{ name: "Robert" });
 *  // Handling data
 *  messaging.on<any>("Field1", (data)=>{ console.log(data.name); } ) // prints "Robert"
 *  // Deleting subject
 *  messaging.off("Field1");
 *  // Disposing NgUiContribMessaging();
 *  messaging.dispose();
 * ```
 *
 *
 * ```
 *  import {IMessaging} from "wi-studio/common/models/messaging";
 *  import {NgUiContribMessaging} from "wi-studio/app/contrib/wi-contrib";
 *  @Injectable()
 *  @TciContrib({})
 *
 *  export class RestTriggerService extends TciServiceHandlerContribution {
 *    private messaging: IMessaging;
 *    constructor() {
 *       messaging = new NgUiContribMessaging();
 *       super();
 *    }
 *
 *    value = (fieldName: string, context: IActivityContribution): Observable<any> | any => {
 *       if (fieldName === "field1") {
 *           let field: IFieldDefinition = context.getField("field1");
 *           this.messaging.emit<any>("field1",{ name: field.value });
 *           return field.value;
 *       } else if (fieldName === "field2") {
 *          return Observable.create(observer => {
 *              this.messaging.on<any>("field1", (data)=>{
 *                  console.log(data.name);  // prints "Robert"
 *                  // Calculate the field2 data
 *                  observer.next("<FIELD2DATA>");
 *                  // The user can delete the subject using this.messaging.off("field1"); if field2 is the only subscriber
 *                  // In case of multiple subscribers there is no need to delete as NgUiContribMessaging uses rxjs/BehaviorSubject
 *                  // which only returns the last emitted value to all subscribers
 *              });
 *          });
 *       }
 *       return null;
 *    }
 *
 *    ...
 *  }
 *
 * ```
 */
export class TciContribMessaging extends AbstractMessaging {
    constructor() {
        super();
    }

    public newSubject<T>(): Subject<T> {
        return new BehaviorSubject<T>(null);
    }
}


@Injectable()
export abstract class AbstractTciContribModelService implements ITCIModelService {

    protected contribService: TciContribService;

    /**
     * Bind ContribService to ModelService
     * @param {ContribService} service
     */
    bindContribService(service: TciContribService) {
        if (!this.contribService) {
            this.contribService = service;
        }
    }

    /**
     * Get ContribService
     * @return {ContribService}
     */
    getUIContribService(): TciContribService {
        if (!this.contribService) {
            throw Error("Null ContribService");
        }
        return this.contribService;
    }

    /**
     * Returns TCI Applications from all tenants
     */
    abstract getApplications(): Observable<ITCIApplication>;

}

@Injectable()
export class TciContribModelService extends AbstractTciContribModelService {

    private static instance: TciContribModelService;

    public static getInstance(): TciContribModelService {
        if (!TciContribModelService.instance) {
            TciContribModelService.instance = new TciContribModelService();
        }
        return TciContribModelService.instance;
    }

    getApplications(): Observable<ITCIApplication> {
        return undefined;
    }

}
